/* eslint-env browser */

export const CAMPAIGN_PARAMETERS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
];

const STORAGE_KEY = "sf_cp";
const MAX_AGE = 30 * 60 * 1000;
let campaign = null;
let permitted = false;
let declined = false;

function parameters(query) {
  const values = {};
  CAMPAIGN_PARAMETERS.forEach((key) => {
    const value = query[key];
    if (typeof value === "string" && value.trim()) values[key] = value.trim().slice(0, 200);
  });
  return values;
}

function fresh(record) {
  return record && Number.isFinite(record.createdAt)
    && record.createdAt <= Date.now() && Date.now() - record.createdAt < MAX_AGE;
}

// Encode UTF-8 before Base64 so campaign names can contain non-ASCII characters.
// This obscures the stored value; it does not encrypt it.
function encodeRecord(record) {
  const bytes = new TextEncoder().encode(JSON.stringify(record));
  return btoa(String.fromCharCode(...bytes));
}

function decodeRecord(value) {
  const bytes = Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
  return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
}

function persist() {
  try {
    if (permitted && campaign) localStorage.setItem(STORAGE_KEY, encodeRecord(campaign));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // In-memory attribution still works when browser storage is blocked.
  }
}

// Read before the router/Auth0 can replace the entry URL. The campaign cache is
// consent-only; the OAuth return state below carries just this allowlisted context.
const query = new URLSearchParams(window.location.search);
let pending = CAMPAIGN_PARAMETERS.some((key) => query.has(key)) ? {
  values: parameters(Object.fromEntries(CAMPAIGN_PARAMETERS.map((key) => [key, query.get(key)]))),
  createdAt: Date.now(),
  accountId: null,
} : null;

export function clearCampaign() {
  campaign = null;
  pending = null;
  persist();
}

export function applyCampaignConsent(next, previous, metadata = {}) {
  permitted = next.analytics;
  declined = next.decided === true && !permitted;
  if (!permitted) {
    if (next.decided || (previous && previous.analytics) || metadata.source === "logout") {
      clearCampaign();
    }
    return;
  }

  if (pending) {
    // A new campaign replaces the entire old set, including missing fields.
    campaign = fresh(pending) ? pending : null;
    pending = null;
  } else if (!campaign) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const record = stored ? decodeRecord(stored) : null;
      if (fresh(record) && record.values && typeof record.values === "object") {
        campaign = {
          values: parameters(record.values),
          createdAt: record.createdAt,
          accountId: typeof record.accountId === "string" ? record.accountId : null,
        };
      }
    } catch (error) {
      // Missing, invalid or unavailable storage means no saved campaign.
    }
  }
  if (!fresh(campaign)) campaign = null;
  persist();
}

export function bindCampaignAccount(id) {
  if (!campaign || !id) return;
  if (campaign.accountId && campaign.accountId !== String(id)) {
    clearCampaign();
    return;
  }
  campaign.accountId = String(id);
  persist();
}

export function captureRouteCampaign(route) {
  if (declined) return;
  const routeQuery = route.query || {};
  const arriving = CAMPAIGN_PARAMETERS.some((key) => (
    Object.prototype.hasOwnProperty.call(routeQuery, key)
  ));
  if (!arriving) return;
  pending = { values: parameters(routeQuery), createdAt: Date.now(), accountId: null };
  // Route query parameters are another explicit campaign arrival, never a merge.
  if (permitted) applyCampaignConsent({ analytics: true });
}

export function campaignProperties() {
  if (!permitted) return {};
  if (!fresh(campaign)) {
    clearCampaign();
    return {};
  }
  return { ...campaign.values };
}

export function campaignForLogin() {
  if (declined) return undefined;
  const record = pending || (permitted && campaign);
  if (!fresh(record)) return undefined;
  return { values: parameters(record.values), createdAt: record.createdAt };
}

export function restoreLoginCampaign(record) {
  if (declined) return;
  if (!fresh(record) || !record.values || typeof record.values !== "object") return;
  pending = { values: parameters(record.values), createdAt: record.createdAt, accountId: null };
  if (permitted) applyCampaignConsent({ analytics: true });
}
